import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight, BadgePercent } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [refInfo, setRefInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');

  // Validate referral code on mount so we can show "Parrainé par X"
  useEffect(() => {
    if (!refCode) return;
    // Public-ish endpoint — fetch without auth, ignore failures
    axios.get(`${API}/billing/referral/validate`, { params: { code: refCode } })
      .then(r => setRefInfo(r.data))
      .catch(() => setRefInfo({ valid: false, code: refCode }));
  }, [refCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    const result = await register(name, email, password);

    if (result.success) {
      // Capture referral if a valid code was passed in the URL
      if (refCode && refInfo?.valid && result.user?.id) {
        try {
          await axios.post(`${API}/billing/referral/capture`, {
            user_id: result.user.id,
            referral_code: refCode,
          });
        } catch { /* silent — referral is best-effort */ }
      }
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="register-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
              <span className="text-white font-heading font-bold">SEO</span>
            </div>
            <span className="font-heading font-bold text-xl text-neutral-900">Agency</span>
          </div>

          <h1 className="font-heading font-bold text-3xl text-neutral-900 mb-2">
            Créer un compte
          </h1>
          <p className="text-neutral-500 mb-8">
            Rejoignez notre plateforme de gestion SEO
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          {refInfo?.valid && (
            <div className="bg-violet-50 border border-violet-200 text-violet-900 px-4 py-3 rounded mb-6 flex items-start gap-2 text-sm" data-testid="referral-banner">
              <BadgePercent className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Parrainé par <strong>{refInfo.referrer_name}</strong> · <strong>-50%</strong> sur votre 1er mois.
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-neutral-700 font-medium">
                Nom complet
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jean Dupont"
                className="mt-2 h-12 border-neutral-200 focus:border-[#002FA7] focus:ring-[#002FA7]"
                required
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-neutral-700 font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@example.com"
                className="mt-2 h-12 border-neutral-200 focus:border-[#002FA7] focus:ring-[#002FA7]"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-neutral-700 font-medium">
                Mot de passe
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  className="h-12 border-neutral-200 focus:border-[#002FA7] focus:ring-[#002FA7] pr-12"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#002FA7] hover:bg-[#002277] text-white font-medium"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Création...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Créer mon compte
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-neutral-500">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-[#002FA7] hover:underline font-medium" data-testid="login-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex flex-1 bg-neutral-900 items-center justify-center p-12">
        <div className="max-w-lg">
          <div className="text-[#002FA7] font-mono text-sm mb-4 tracking-wider">
            // NOUVELLE AGENCE
          </div>
          <h2 className="font-heading font-bold text-4xl text-white mb-6">
            Démarrez avec la plateforme SEO la plus précise
          </h2>
          <p className="text-neutral-400 leading-relaxed">
            Centralisez la gestion de vos clients, le suivi des positions, 
            et la génération de rapports professionnels.
          </p>
          
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-4 text-white">
              <div className="w-8 h-8 border border-[#002FA7] rounded flex items-center justify-center text-[#002FA7] font-mono">1</div>
              <span>Ajoutez vos clients et leurs sites</span>
            </div>
            <div className="flex items-center gap-4 text-white">
              <div className="w-8 h-8 border border-[#002FA7] rounded flex items-center justify-center text-[#002FA7] font-mono">2</div>
              <span>Configurez les mots-clés à suivre</span>
            </div>
            <div className="flex items-center gap-4 text-white">
              <div className="w-8 h-8 border border-[#002FA7] rounded flex items-center justify-center text-[#002FA7] font-mono">3</div>
              <span>Générez des rapports automatiques</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
